import { trackInteraction } from 'client/javascript/analytics';

const SELECTORS = {
  wrapper: '.select-with-groups',
  description: '.select-with-groups__selected-element-description',
  selectedName: '.select-with-groups__selected-element-name',
  selectItems: '.select-with-groups__dropdown-element',
  selectedItemName: '.select-with-groups__dropdown-element-name',
  selected: '.select-with-groups__dropdown-element--selected',
  button: '.select-with-groups__search-button',
  dropdown: '.select-with-groups__dropdown',
  dropdownGroups: '.select-with-groups__dropdown-groups'
};

const KEYCODE = {
  enter: 13,
  escape: 27,
  firstLetter: 65,
  arrowUp: 38,
  arrowDown: 40
};

SELECTORS.selectedClass = SELECTORS.selected.slice(1);

export class SelectWithGroups {
  static create(options) {
    return new SelectWithGroups(options);
  }

  constructor({ wrapperElement, onSubmit = () => {}, onSelect = this.set, syncElement = {}, updateClosestMarketId }) {
    this.button = wrapperElement.querySelector(SELECTORS.button);
    this.dropdown = wrapperElement.querySelector(SELECTORS.dropdown);
    this.clickableDescription = wrapperElement.querySelector(SELECTORS.description);
    this.selectedNameElement = wrapperElement.querySelector(SELECTORS.selectedName);
    this.updateClosestMarketId = updateClosestMarketId;

    this.syncElement = syncElement;

    this.isDropdownShown = false;
    this.selectedItem = null;

    this.highlightedElement = null;
    this.searchString = '';

    this.synchronizer = this.synchronizer.bind(this);
    this.handleDropdownClick = this.handleDropdownClick.bind(this);
    this.outsideClickHandler = this.outsideClickHandler.bind(this);
    this.searchLocation = this.searchLocation.bind(this);
    this.set = this.set.bind(this);
    this.onSelect = onSelect.bind(this);

    // set initial value
    const initialDataId = this.dropdown.querySelector('li:first-child').getAttribute('data-id') || '';
    this.set(initialDataId);

    // handle open/close dropdown
    this.clickableDescription.addEventListener('click', this.handleDropdownClick);

    // handle dropdown items selection
    const setByDataId = e =>
      this.onSelect(e.target.getAttribute('data-id') || e.target.parentElement.getAttribute('data-id'));
    Array.from(this.dropdown.querySelectorAll('li')).forEach(li => li.addEventListener('click', setByDataId));

    // handle search button click
    this.button.addEventListener('click', e => {
      const id = this.selectedItem.getAttribute('data-id') || '';

      trackInteraction({ label: `${this.button.dataset.name} Click Submit`, selectionName: id });

      onSubmit(e, {
        id,
        name: this.selectedItem.innerHTML || '',
        value: this.selectedItem.getAttribute('data-value') || ''
      });
    });
  }

  updateSyncedElement(dataId) {
    const selectedElement = this.syncElement.querySelector(SELECTORS.selectedName);
    this.syncElement.querySelector(`[data-id="${dataId}"]`).classList.add(SELECTORS.selectedClass);
    const selectedItemName = this.selectedItem.querySelector(SELECTORS.selectedItemName).innerHTML;
    selectedElement.innerHTML = selectedItemName;
    selectedElement.setAttribute('title', selectedItemName);
  }

  scrollToElement(scroll) {
    const dropdown = this.syncElement.querySelector(SELECTORS.dropdown);

    if (scroll !== 0) {
      dropdown.style.display = 'block';
      const scrollable = dropdown.querySelector(SELECTORS.dropdownGroups);
      scrollable.scrollTop = scroll;
      dropdown.style.display = 'none';
    }
  }

  synchronizer(dataId, scroll = 0) {
    if (this.syncElement) {
      const previousSelectedItem = this.syncElement.querySelector(SELECTORS.selected);

      if (previousSelectedItem) {
        this.syncElement.querySelector(SELECTORS.selected).classList.remove(SELECTORS.selectedClass);
      }

      this.updateSyncedElement(dataId);
      this.scrollToElement(scroll);
    }
  }

  setHighlight() {
    if (this.highlightedElement) {
      const scrollable = this.dropdown.querySelector(SELECTORS.dropdownGroups);
      scrollable.scrollTop = this.highlightedElement.offsetTop - this.highlightedElement.offsetHeight * 2;
      this.highlightedElement.classList.add('select-with-groups__highlight');
    }
  }

  searchLocation(event) {
    const { keyCode, key } = event;
    const dropdownList = Array.from(this.dropdown.querySelectorAll(SELECTORS.selectItems));
    let timeOut = null;

    // Remove highlighting
    if (this.highlightedElement) {
      this.highlightedElement.classList.remove('select-with-groups__highlight');
    }

    // Set element on Enter press
    if (keyCode === KEYCODE.enter) {
      if (this.highlightedElement) {
        const selectedItemId = this.highlightedElement.getAttribute('data-id');
        this.set(selectedItemId);
        this.onSelect(selectedItemId);
      }
    }

    // Close dropdown on Esc press
    if (keyCode === KEYCODE.escape) {
      this.toggleDropdown();
    }

    // Letter navigation
    if (keyCode >= KEYCODE.firstLetter) {
      clearTimeout(timeOut);
      this.searchString += key.toLowerCase();

      this.highlightedElement = dropdownList.find(
        listItem =>
          listItem
            .getAttribute('title')
            .toLowerCase()
            .indexOf(this.searchString) === 0
      );

      this.setHighlight();

      timeOut = setTimeout(() => {
        this.searchString = '';
      }, 700);
    }

    // Arrow navigation
    if (keyCode === KEYCODE.arrowUp || keyCode === KEYCODE.arrowDown) {
      event.preventDefault();
      let indexOfhighlightedElement;

      if (this.highlightedElement) {
        indexOfhighlightedElement = dropdownList.findIndex(
          item => item.getAttribute('data-id') === this.highlightedElement.getAttribute('data-id')
        );
      }

      if (keyCode === KEYCODE.arrowUp) {
        // Move up on list
        this.highlightedElement = this.highlightedElement
          ? dropdownList[indexOfhighlightedElement - 1]
          : dropdownList[dropdownList.length - 1];
        this.setHighlight();
      } else {
        // Move down of list
        this.highlightedElement = this.highlightedElement
          ? dropdownList[indexOfhighlightedElement + 1]
          : dropdownList[0];
        this.setHighlight();
      }
    }
  }

  set(dataId) {
    if (!dataId) {
      return undefined;
    }

    if (this.selectedItem) {
      Array.from(this.dropdown.querySelectorAll(SELECTORS.selected)).forEach(item =>
        item.classList.remove(SELECTORS.selectedClass)
      );
    }

    this.selectedItem = this.dropdown.querySelector(`[data-id="${dataId}"]`);

    this.selectedItem.classList.add(SELECTORS.selectedClass);

    const selectedItemName = this.selectedItem.querySelector(SELECTORS.selectedItemName).innerHTML;
    this.selectedNameElement.innerHTML = selectedItemName;
    this.selectedNameElement.setAttribute('title', selectedItemName);

    const scrollable = this.dropdown.querySelector(SELECTORS.dropdownGroups);

    if (this.isDropdownShown) {
      this.synchronizer(dataId, scrollable.scrollTop);
      this.toggleDropdown();
    } else {
      // need to open dropdown for element to scroll into view
      this.toggleDropdown();
      const scrollableBoundary = scrollable.getBoundingClientRect();
      const itemBoundary = this.selectedItem.getBoundingClientRect();
      const windowScroll = window.scrollY || window.pageYOffset; // this value is used to make measurements absolute
      scrollable.scrollTop =
        itemBoundary.top + windowScroll - (scrollableBoundary.bottom + windowScroll) + itemBoundary.height * 4;
      this.toggleDropdown();
    }
  }

  toggleDropdown() {
    this.isDropdownShown = !this.isDropdownShown;

    if (this.isDropdownShown) {
      this.dropdown.style.display = 'block';
      document.addEventListener('keydown', this.searchLocation);
      return document.addEventListener('click', this.outsideClickHandler);
    }

    this.dropdown.style.display = 'none';
    document.removeEventListener('click', this.outsideClickHandler);
    document.removeEventListener('keydown', this.searchLocation);
  }

  handleDropdownClick(event) {
    if (event) {
      event.preventDefault();
    }

    trackInteraction({ label: `${this.button.dataset.name} Click` });
    this.toggleDropdown();
  }

  outsideClickHandler(event) {
    if (
      !this.button.parentElement.contains(event.target) ||
      this.selectedItem.contains(event.target) ||
      !event.target.closest(SELECTORS.description)
    ) {
      this.toggleDropdown();
    }
  }
}
